import React, { useState, useEffect } from 'react';
import DataTable from "./datatable";
import {fetchStockPrice, fetchTable} from "../api";
import { ArrowUp, ArrowDown, Minus, Circle } from "lucide-react";
import SignalTable from "./signaltable";



// helper: derive signals from numbers
// function to transform raw JSON from pandas orient="split"

function transformTAJson(jsonData) {
  if (!jsonData || !Array.isArray(jsonData.columns) || !Array.isArray(jsonData.data)) {
    console.error("Invalid jsonData passed to transformTAJson:", jsonData);
    return [];
  }

  const { columns, data } = jsonData;

  // helper to safely get column value
  const getVal = (row, colName) => {
    const idx = columns.indexOf(colName);
    return idx !== -1 ? row[idx] : undefined;
  };

  const getSummaryClass = (summary) => {
  switch (summary) {
    case "Strong Buy":
      return "dark-green";
    case "Buy":
      return "light-green";
    case "Strong Sell":
      return "dark-red";
    case "Sell":
      return "light-red";
    case "Neutral":
    default:
      return "gray";
  }
};

  // helper function
  const deriveSignals = (row) => {
    const close = getVal(row, "Close");
    const superTrend = getVal(row, "SuperTrend");
    const macd = getVal(row, "MACD");
    const bbL = getVal(row, "BollingB_L");
    const bbU = getVal(row, "BollingB_U");
    const stochK = getVal(row, "STOCH_RSI_k");
    const stochD = getVal(row, "STOCH_RSI_d");

    if ([close, superTrend, macd, bbL, bbU, stochK, stochD].some(v => v === undefined)) {
      return {
        superTrend: { text: "N/A", icon: "Minus", value: null },
        macd: { text: "N/A", icon: "Minus", value: null },
        stoch: { text: "N/A", icon: "Minus", value: null },
        bollinger: { text: "N/A", icon: "Minus", value: null },
        summary: { text: "N/A", badge: "gray" }
      };
    }

    // --- SuperTrend
    const superTrendSignal = close > superTrend ? "Buy" : "Sell";
    const superTrendIcon = close > superTrend ? "ArrowUp" : "ArrowDown";

    // --- MACD
    let macdSignal = "Neutral";
    let macdIcon = "Minus";
    if (macd > 0) {
      macdSignal = "Bullish";
      macdIcon = "ArrowUp";
    } else if (macd < 0) {
      macdSignal = "Bearish";
      macdIcon = "ArrowDown";
    }

    // --- STOCH RSI
    let stochSignal = "Neutral";
    let stochIcon = "Minus";
    if (stochK > 80 && stochD > 80) {
      stochSignal = "Overbought";
      stochIcon = "ArrowUp";
    } else if (stochK < 20 && stochD < 20) {
      stochSignal = "Oversold";
      stochIcon = "ArrowDown";
    }

    // --- Bollinger Bands
    let bollingerSignal = "Mid Band";
    let bollingerIcon = "Minus";

    const upper = bbU;
    const lower = bbL;
    const mid = (upper + lower) / 2;

    // Define "near" threshold (2% of band width)
    const threshold = (upper - lower) * 0.02;

    if (close >= upper - threshold) {
      bollingerSignal = "Near Upper";
      bollingerIcon = "Circle";
    } else if (close <= lower + threshold) {
      bollingerSignal = "Near Lower";
      bollingerIcon = "Circle";
    } else if (close > mid) {
      bollingerSignal = "Above Mid";
      bollingerIcon = "ArrowUp";
    } else if (close < mid) {
      bollingerSignal = "Below Mid";
      bollingerIcon = "ArrowDown";
    }

    // --- Composite Summary
    const signals = [superTrendSignal, macdSignal, stochSignal, bollingerSignal];
    const score =
      signals.filter((s) =>
        ["Buy", "Bullish", "Oversold", "Near Lower"].includes(s)
      ).length -
      signals.filter((s) =>
        ["Sell", "Bearish", "Overbought", "Near Upper"].includes(s)
      ).length;

    let summary = "Neutral";
    let badge = "gray";
    if (score >= 2) {
      summary = "Buy";
      badge = getSummaryClass(summary);
    }
    if (score >= 3) {
      summary = "Strong Buy";
      badge = getSummaryClass(summary);
    }
    if (score <= -2) {
      summary = "Sell";
      badge = getSummaryClass(summary);
    }
    if (score <= -3) {
      summary = "Strong Sell";
      badge = getSummaryClass(summary);
    }

    return {
      superTrend: { text: superTrendSignal, icon: superTrendIcon, value: superTrend },
      macd: { text: macdSignal, icon: macdIcon, value: macd },
      stoch: { text: stochSignal, icon: stochIcon, value: { k: stochK, d: stochD } },
      bollinger: { text: bollingerSignal, icon: bollingerIcon, value: { lower: bbL, upper: bbU } },
      summary: { text: summary, badge },
    };
  };

  // transform each row safely
  return data.map((row) => {
    const sec = getVal(row, "Security");
    const close = getVal(row, "Close");
    const signals = deriveSignals(row);

    return {
      security: sec ?? "Unknown",
      close,
      ...signals,
    };
  });
}

function DisplayTable({table,symbol="",tdata=""}) {
  const [tableData, setTableData] = useState(null);
  const url = 'http://127.0.0.1:8000';

  useEffect(() =>{
    // Simulate fetching data from API
  if (tdata !== "") {
    const tableData = JSON.parse(tdata);
    setTableData(tableData);
  } else {
    fetchTable(table, symbol).then((data) => {
      let tableData;
      if (table === "ta") {
        console.log("SANTOSH" , data);

        // transform TA JSON
        tableData = transformTAJson(JSON.parse(data));
        console.log("SANTOSH TRANSFORMED DATA" , tableData);

      } else {
        tableData = JSON.parse(data);
      }

      setTableData(tableData);
      // console.log("SANTOSH" , tableData.data);
    });
  }}, [symbol]);

  if (!tableData) {
    return <div>Loading data...</div>;
  }

  const transformToSignalTableFormat = (tableData) => {
  return {
    columns: ["Security", "SuperTrend", "MACD", "Stoch"],
    data: tableData.map(row => [
      row.security,
      row.superTrend?.signal === "BUY" ? "Bullish" : row.superTrend?.signal === "SELL" ? "Bearish" : "Neutral",
      row.macd?.signal === "BUY" ? "Bullish" : row.macd?.signal === "SELL" ? "Bearish" : "Neutral",
      row.stoch?.signal === "BUY" ? "Bullish" : row.stoch?.signal === "SELL" ? "Bearish" : "Neutral"
    ])
  };
};

return (
  <div>
    {table === "ta" ? (
      <SignalTable data={tableData} />
    ) : (
      <DataTable data={tableData} table={table} />
    )}
  </div>
);
}

export default DisplayTable;

